<script lang="ts" setup>
import { computed } from "vue";
import type {
  RunStatus,
  TimeShortcut,
} from "@/views/builder/composables/useHistoryManagement";
import { ALLOWED_PAGE_SIZES } from "@/views/builder/composables/useHistoryManagement";

interface WorkflowOption {
  value: string;
  label: string;
}

const props = withDefaults(
  defineProps<{
    searchQuery: string;
    statusFilters: RunStatus[];
    timeShortcut: TimeShortcut;
    dateFrom: Date | null;
    dateTo: Date | null;
    workflowFilter: string;
    workflowOptions: WorkflowOption[];
    /** Product filter (org-wide Run History only). Same {value,label} shape as workflowOptions. */
    productFilter?: string;
    productOptions?: WorkflowOption[];
    sortBy: string;
    pageSize: number;
    advancedOpen: boolean;
    showWorkflowFilter?: boolean;
    /** Show the product-filter dropdown — only the org-wide Run History page sets this. */
    showProductFilter?: boolean;
    /** Grouped view: nest child runs under their parent instead of listing them flat. */
    grouped?: boolean;
  }>(),
  {
    showWorkflowFilter: true,
    productFilter: "all",
    productOptions: () => [],
    showProductFilter: false,
    grouped: true,
  }
);

const emit = defineEmits<{
  updateSearchQuery: [value: string];
  toggleStatus: [status: RunStatus];
  updateTimeShortcut: [shortcut: TimeShortcut];
  updateDateRange: [from: Date | null, to: Date | null];
  updateWorkflowFilter: [workflowId: string];
  updateProductFilter: [productId: string];
  updateSortBy: [value: string];
  updatePageSize: [size: number];
  updateAdvancedOpen: [open: boolean];
  updateGrouped: [value: boolean];
  reset: [];
}>();

const statusOptions: {
  value: RunStatus;
  label: string;
  icon: string;
  cls: string;
}[] = [
  { value: "Succeeded", label: "Success", icon: "bi-check-lg", cls: "status-success" },
  { value: "Failed", label: "Failed", icon: "bi-x-lg", cls: "status-danger" },
  { value: "Warning", label: "Warning", icon: "bi-exclamation-lg", cls: "status-warning" },
  { value: "Pending", label: "Pending", icon: "bi-clock", cls: "status-secondary" },
];

const timeOptions: { value: TimeShortcut; label: string }[] = [
  { value: "1h", label: "1h" },
  { value: "1d", label: "1d" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
];

const isStatusActive = (s: RunStatus) => props.statusFilters.includes(s);

function toIsoDate(d: Date | null): string {
  if (!d) return "";
  // yyyy-mm-dd in local time for <input type="date">
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const dateFromStr = computed(() => toIsoDate(props.dateFrom));
const dateToStr = computed(() => toIsoDate(props.dateTo));

function parseDateInput(value: string): Date | null {
  if (!value) return null;
  // Date constructed from yyyy-mm-dd is local midnight in modern browsers.
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

function onDateFromChange(e: Event) {
  const v = (e.target as HTMLInputElement).value;
  emit("updateDateRange", parseDateInput(v), props.dateTo);
}

function onDateToChange(e: Event) {
  const v = (e.target as HTMLInputElement).value;
  emit("updateDateRange", props.dateFrom, parseDateInput(v));
}

const isCustomTimeActive = computed(() => props.timeShortcut === "custom");
</script>

<template>
  <div class="run-history-filters px-3 pb-2 border-bottom flex-shrink-0">
    <!-- Search -->
    <div class="input-group input-group-sm mb-2">
      <span class="input-group-text">
        <i class="bi bi-search"></i>
      </span>
      <input
        :value="searchQuery"
        @input="emit('updateSearchQuery', ($event.target as HTMLInputElement).value)"
        type="text"
        class="form-control"
        placeholder="Search runs…"
      />
    </div>

    <!-- Row: status icons + time pills -->
    <div class="d-flex align-items-center gap-2 mb-2 flex-wrap">
      <div class="d-flex gap-1 align-items-center">
        <button
          v-for="opt in statusOptions"
          :key="opt.value"
          type="button"
          class="status-icon-btn"
          :class="[opt.cls, { 'is-off': !isStatusActive(opt.value) }]"
          :aria-pressed="isStatusActive(opt.value)"
          :title="opt.label"
          @click="emit('toggleStatus', opt.value)"
        >
          <i class="bi" :class="opt.icon"></i>
        </button>
      </div>
      <div class="btn-group btn-group-sm ms-auto" role="group" aria-label="Time window">
        <button
          v-for="t in timeOptions"
          :key="t.value"
          type="button"
          class="btn time-pill"
          :class="timeShortcut === t.value ? 'btn-primary' : 'btn-outline-primary'"
          :title="isCustomTimeActive ? 'Custom date range active — see Advanced' : t.label"
          @click="emit('updateTimeShortcut', t.value)"
        >
          {{ t.label }}
        </button>
      </div>
    </div>

    <!-- Row: product/workflow + sort -->
    <div class="d-flex align-items-center gap-2 mb-2">
      <select
        v-if="showProductFilter"
        :value="productFilter"
        @change="emit('updateProductFilter', ($event.target as HTMLSelectElement).value)"
        class="form-select form-select-sm flex-grow-1"
        style="min-width: 0;"
        aria-label="Product filter"
      >
        <option v-for="opt in productOptions" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>
      <select
        v-if="showWorkflowFilter"
        :value="workflowFilter"
        @change="emit('updateWorkflowFilter', ($event.target as HTMLSelectElement).value)"
        class="form-select form-select-sm flex-grow-1"
        style="min-width: 0;"
        aria-label="Workflow filter"
      >
        <option v-for="opt in workflowOptions" :key="opt.value" :value="opt.value">
          {{ opt.label }}
        </option>
      </select>
      <select
        :value="sortBy"
        @change="emit('updateSortBy', ($event.target as HTMLSelectElement).value)"
        class="form-select form-select-sm sort-select"
        aria-label="Sort runs"
      >
        <option value="newest">↓ Newest</option>
        <option value="oldest">↑ Oldest</option>
        <option value="duration">⏱ Duration</option>
      </select>
    </div>

    <!-- Advanced section (toggle lives in HistoryPanel header) -->
    <div v-if="advancedOpen" class="advanced-section pt-2 mt-1 border-top">
      <div class="form-check form-switch mb-2">
        <input
          id="group-child-runs"
          class="form-check-input"
          type="checkbox"
          role="switch"
          :checked="grouped"
          @change="emit('updateGrouped', ($event.target as HTMLInputElement).checked)"
        />
        <label class="form-check-label small" for="group-child-runs">Group child runs under parents</label>
      </div>

      <div class="row g-2 mb-2">
        <div class="col-6">
          <input
            type="date"
            class="form-control form-control-sm"
            :value="dateFromStr"
            @change="onDateFromChange"
            aria-label="From date"
            title="From"
          />
        </div>
        <div class="col-6">
          <input
            type="date"
            class="form-control form-control-sm"
            :value="dateToStr"
            @change="onDateToChange"
            aria-label="To date"
            title="To"
          />
        </div>
      </div>

      <div class="d-flex align-items-center gap-2 mb-2">
        <span class="small text-muted text-nowrap">Results</span>
        <div class="btn-group btn-group-sm flex-grow-1" role="group" aria-label="Maximum results to fetch">
          <button
            v-for="size in ALLOWED_PAGE_SIZES"
            :key="size"
            type="button"
            class="btn time-pill"
            :class="pageSize === size ? 'btn-primary' : 'btn-outline-primary'"
            @click="emit('updatePageSize', size)"
          >
            {{ size }}
          </button>
        </div>
        <button
          type="button"
          class="btn btn-sm btn-link p-0 text-decoration-none text-nowrap"
          title="Reset filters"
          @click="emit('reset')"
        >
          <i class="bi bi-arrow-counterclockwise"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.status-icon-btn {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  line-height: 1;
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.1s ease;
  padding: 0;
}

.status-icon-btn:hover {
  transform: scale(1.08);
}

.status-icon-btn.is-off {
  opacity: 0.4;
  filter: grayscale(0.4);
}

.status-icon-btn:focus-visible {
  outline: 2px solid var(--bs-primary);
  outline-offset: 2px;
}

// Match the pastel badge palette used throughout the app
// (see App/src/scss/styles.scss .badge.bg-*)
.status-success {
  background: #dcfce7;
  color: #166534;
  border-color: #bbf7d0;
}

.status-danger {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecaca;
}

.status-warning {
  background: #fef3c7;
  color: #d97706;
  border-color: #fde68a;
}

.status-secondary {
  background: #f1f5f9;
  color: #64748b;
  border-color: #e2e8f0;
}

// Dark mode — the light pastels above read as washed-out on the dark surface. Mirror the
// dark badge palette (see App/src/scss/styles.scss [data-bs-theme="dark"] .badge.bg-*) so these
// filter chips match the status badges they echo.
[data-bs-theme="dark"] {
  .status-success {
    background: #103123;
    color: #75d99b;
    border-color: #1c4d34;
  }

  .status-danger {
    background: #3a1a1a;
    color: #f5a3a3;
    border-color: #5c2a2a;
  }

  .status-warning {
    background: #382c10;
    color: #f2c14e;
    border-color: #5a481c;
  }

  .status-secondary {
    background: #262b31;
    color: #9aa5b1;
    border-color: #3a424c;
  }
}

.time-pill {
  font-size: 0.75rem;
  padding: 0.15rem 0.45rem;
  min-width: 34px;
}

.sort-select {
  width: auto;
  flex-shrink: 0;
  font-size: 0.8rem;
}

.advanced-section {
  border-top-color: var(--bs-border-color) !important;
}
</style>
